import { useState, ReactNode } from 'react'
import { useTranslation } from 'next-i18next'

import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ChevronLeftIcon from '~/components/Icons/svgs/ChevronLeftIcon'
import ChevronRightIcon from '~/components/Icons/svgs/ChevronRightIcon'
import { DIRECTION } from '~/shared/types/pagination.types'
import ROUTES from '~/shared/constants/routes.const'
import { Link } from '~/components/Link'

import Pages from './Pages'

export default function Pagination({
  currPage,
  onDecreasePageClick,
  onIncreasePageClick,
  onSetPageClick,
  totalPages,
  withLink = false,
  url,
  baseUrl,
  squreDesign = false,
}: {
  currPage: number
  onDecreasePageClick?: () => void
  onIncreasePageClick?: () => void
  onSetPageClick?: (newPage: number) => void
  totalPages: number
  withLink?: boolean
  url?: string
  baseUrl?: string
  squreDesign?: boolean
}): React.ReactElement | null {
  const { t } = useTranslation('generics')
  const [navigationDirection, setNavigationDirection] = useState<DIRECTION | null>(null)

  const isDecreaseDisabled = currPage === 0
  const isIncreaseDisabled = currPage === totalPages - 1

  const lessThan10Pages = totalPages < 10

  const prevPageUrl = `${url}/${currPage}` === baseUrl ? ROUTES.OUR_TEAM : `${url}/${currPage}`
  const nextPageUrl = `${url}/${currPage + 2}` === baseUrl ? ROUTES.OUR_TEAM : `${url}/${currPage + 2}`

  enum navButtonType {
    PREVIOUS,
    NEXT,
  }

  const handleOnDecreasePageClick = (): void => {
    setNavigationDirection(DIRECTION.UP)
    if (onDecreasePageClick) {
      onDecreasePageClick()
    }
  }

  const handleOnIncreasePageClick = (): void => {
    setNavigationDirection(DIRECTION.DOWN)
    if (onIncreasePageClick) {
      onIncreasePageClick()
    }
  }

  const handleOnSetPageClick = (newPage: number): void => {
    setNavigationDirection(newPage < currPage ? DIRECTION.UP : DIRECTION.DOWN)
    if (onSetPageClick) {
      onSetPageClick(newPage)
    }
  }

  if (totalPages < 1) {
    return null
  }

  function Wrapper({ children }: { children: ReactNode }): React.ReactElement {
    return (
      <nav aria-label="Pagination" className={`relative z-0 w-max flex items-center ${squreDesign ? '' : 'space-x-2'}`}>
        {children}
      </nav>
    )
  }

  const sharedClassNames = `${
    squreDesign
      ? 'bg-gradient-purple-light-top p-px'
      : 'rounded-full border border-gray-light bg-gray-off-white h-10 hover:bg-purple-light disabled:opacity-50 disabled:bg-white'
  } relative w-10 flex items-center justify-center`

  function NavButton({ buttonType }: { buttonType: navButtonType }): React.ReactElement {
    const roundingClassName = buttonType === navButtonType.PREVIOUS ? 'rounded-l' : 'rounded-r'
    return withLink ? (
      <Link
        disabled={buttonType === navButtonType.PREVIOUS ? isDecreaseDisabled : isIncreaseDisabled}
        to={buttonType === navButtonType.PREVIOUS ? prevPageUrl : nextPageUrl}
      >
        <button
          className={`${sharedClassNames} ${squreDesign ? roundingClassName : ''}`}
          disabled={buttonType === navButtonType.PREVIOUS ? isDecreaseDisabled : isIncreaseDisabled}
          onClick={buttonType === navButtonType.PREVIOUS ? handleOnDecreasePageClick : handleOnIncreasePageClick}
        >
          <div className={`py-px ${squreDesign ? 'bg-purple-dark-violet' : ''}`}>
            <span className="sr-only">{t('previous')}</span>
            <SVGIcon
              icon={buttonType === navButtonType.PREVIOUS ? ChevronLeftIcon : ChevronRightIcon}
              pathClassName={squreDesign ? 'text-white' : 'text-black'}
              size={12}
            />
          </div>
        </button>
      </Link>
    ) : (
      <button
        className={`${sharedClassNames} ${squreDesign ? roundingClassName : ''}`}
        disabled={buttonType === navButtonType.PREVIOUS ? isDecreaseDisabled : isIncreaseDisabled}
        onClick={buttonType === navButtonType.PREVIOUS ? handleOnDecreasePageClick : handleOnIncreasePageClick}
      >
        <div
          className={`${
            squreDesign
              ? `flex items-center justify-center py-px bg-purple-dark-violet h-10 w-full hover:bg-gradient-purple-light-top disabled:opacity-50 ${roundingClassName}`
              : ''
          }`}
        >
          <span className="sr-only">{t('previous')}</span>
          <SVGIcon
            icon={buttonType === navButtonType.PREVIOUS ? ChevronLeftIcon : ChevronRightIcon}
            pathClassName={squreDesign ? 'text-white' : 'text-black'}
            size={12}
          />
        </div>
      </button>
    )
  }

  return (
    <Wrapper>
      <NavButton buttonType={navButtonType.PREVIOUS} />
      <div
        className={`flex ${
          squreDesign ? 'bg-gradient-purple-light-top py-px gap-px' : 'rounded-full bg-gray-off-white'
        }`}
      >
        <Pages
          baseUrl={baseUrl}
          currPage={currPage}
          lessThan10Pages={lessThan10Pages}
          navigationDirection={navigationDirection}
          onSetPageClick={lessThan10Pages ? onSetPageClick : handleOnSetPageClick}
          squreDesign={squreDesign}
          totalPages={totalPages}
          url={url}
          withLink={withLink}
        />
      </div>
      <NavButton buttonType={navButtonType.NEXT} />
    </Wrapper>
  )
}
